import React, { ComponentProps, CSSProperties, ReactNode } from 'react'
import Skeleton from 'react-loading-skeleton'

import { bemJoin } from 'bem-join'
import { TiArrowSortedDown, TiArrowSortedUp, TiArrowUnsorted } from 'react-icons/ti'

import { SortDirection } from '@app/graphql/types'
import './Table.scss'

export type TableColumn<T> = {
  key: string
  label: ReactNode
  width?: CSSProperties['width']
  justification?: 'left' | 'center' | 'right'
  value: (item: T) => ReactNode
  sort?: SortDirection | null
  onSortChange?: (direction?: SortDirection) => void
}

export type TableProps<T> = {
  items: Array<T>
  columns: Array<TableColumn<T>>
  rowProps?: (item: T) => ComponentProps<'tr'>
  noResults?: ReactNode
  loading?: boolean
}

const b = bemJoin('admin-table')
const noopProps = () => ({})

export const Table = <T,>({
  columns,
  rowProps = noopProps,
  items,
  loading,
  noResults
}: TableProps<T>) => {
  return (
    <div
      className={b({
        loading
      })}
    >
      <table>
        <thead>
          <tr>
            {columns.map(({ key, width, justification = 'left', onSortChange, sort, label }) => (
              <th key={key} data-key={key} style={{ width }}>
                <div
                  className={b('heading', {
                    [justification]: true
                  })}
                >
                  <span>{label}</span>
                  {onSortChange && (
                    <span
                      className={b('sort', {
                        active: !!sort
                      })}
                    >
                      {!sort && (
                        <button onClick={() => onSortChange(SortDirection.Asc)}>
                          <TiArrowUnsorted />
                        </button>
                      )}
                      {sort === SortDirection.Asc && (
                        <button onClick={() => onSortChange(SortDirection.Desc)}>
                          <TiArrowSortedUp />
                        </button>
                      )}
                      {sort === SortDirection.Desc && (
                        <button onClick={() => onSortChange(undefined)}>
                          <TiArrowSortedDown />
                        </button>
                      )}
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {loading &&
            items.length === 0 && [
              [...new Array(5)].map((_, index) => (
                <tr key={index}>
                  {columns.map((column) => (
                    <td key={column.key}>
                      <Skeleton />
                    </td>
                  ))}
                </tr>
              ))
            ]}
          {!loading && items.length === 0 && (
            <tr>
              <td colSpan={columns.length} className={b('cell', { center: true })}>
                {noResults}
              </td>
            </tr>
          )}
          {items.map((item, index) => {
            const hasActionRequired = !!(item as any).actionRequired
            return (
              <tr key={index} {...rowProps(item)}>
                {columns.map((column, columnIndex) => {
                  const isFirstColumn = columnIndex === 0
                  const shouldShowDot = hasActionRequired && isFirstColumn

                  return (
                    <td
                      key={column.key}
                      data-key={column.key}
                      className={b('cell', {
                        [column.justification || 'left']: true
                      })}
                    >
                      {shouldShowDot ? (
                        <div className={b('cell-with-action')}>
                          <span className={b('action-dot')} />
                          <span>{column.value(item)}</span>
                        </div>
                      ) : (
                        column.value(item)
                      )}
                    </td>
                  )
                })}
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
